import { Component, EventEmitter, Input, OnChanges, OnInit, Output, QueryList, SimpleChanges } from '@angular/core';
import { FormGroupDirective, UntypedFormGroup, UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { combineLatest, merge, Observable, of, ReplaySubject, Subscription, throwError } from 'rxjs';
import { toggleEditMode, getExpertMode, getMotorrad, getOffertBase, getVerifyOffert } from '../../../../shared/utils';
import { first, map, flatMap, catchError, take, shareReplay } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';
import { PopupService } from '../../../service/popup.service';
import { MessageService } from '../../../service/message.service';
import { TranslateService } from '@ngx-translate/core';
import { ILookupValue } from '../../../models/lookupvalues';
import { LookupService } from '../../../service/lookup.service';
import { DateService } from '../../../service/date.service';
import { Global } from '../../../../shared/global';
import { LookupGroup, valueGroupOptions } from '../../../../shared/autocomplete';
import { CustomValidator } from '../../../../shared/validation';
import { IKfzRisiko } from '../../../models/kfzrisiko';
import * as moment from 'moment';
import { CalculationService } from '../../../service/calculation.service';
import { HttpErrorResponse } from '@angular/common/http';
import { EurotaxListDialogService } from '../../../service/eurotax-list-dialog.service';
import { ErrorMessageType } from '../../../../shared/enum';
import { IEuroTaxResult } from '../../../models/eurotaxresult';
import { EurotaxService } from '../../../service/eurotax.service';
import { KaskoKfzComponent } from '../kaskokfz/kaskokfz.component';
import { IEuroTaxMappingResult } from '../../../models/eurotaxmappingresult';
import { DataService } from '../../../service/data.service';

@Component({
  selector: 'app-risikokfz',
  templateUrl: './risikokfz.component.html',
  styleUrls: ['./risikokfz.component.scss']
})
export class RisikoKfzComponent implements OnInit, OnChanges {
  @Input()
  parentFormGroupDirective: FormGroupDirective;

  @Input()
  formGroup: UntypedFormGroup;

  @Input()
  risikoKfzComponents: QueryList<RisikoKfzComponent>;

  @Input()
  kaskoKfzComponents: QueryList<KaskoKfzComponent>;

  @Input()
  privatperson: boolean = null;

  @Input()
  einpersonenUnternehmen: boolean = null;

  @Input()
  firma: boolean = null;

  @Input()
  selected = false;

  @Input()
  complete = false;

  @Input()
  idprefix: string;

  @Input()
  fahrzeugarten: ReplaySubject<Array<ILookupValue>>;

  @Input()
  verwendungsbestimmungen: ReplaySubject<Array<ILookupValue>>;

  @Input()
  antriebsarten: ReplaySubject<Array<ILookupValue>>;

  @Output()
  fahrzeugartChangedEvent = new EventEmitter<void>();

  @Output()
  parameterChangedEvent = new EventEmitter<void>();

  erstzulassungsdatumMinDate = Global.ERSTZULASSUNGSDATUM_MIN_DATE;
  erstzulassungsdatumCo2 = Global.ERSTZULASSUNGSDATUM_CO2;

  expertMode: boolean;
  motorrad: boolean;
  offertBase: boolean;
  verifyOffert: boolean;

  marken = new ReplaySubject<Array<ILookupValue>>();
  markeGroupOptions: Observable<LookupGroup[]>;

  eurotaxMarke: Subscription;
  eurotaxAntriebsart: Subscription;
  eurotax = false;

  constructor(private route: ActivatedRoute, public translateService: TranslateService, public popupService: PopupService,
    public lookupService: LookupService, private dateService: DateService, private calculationService: CalculationService,
    public eurotaxListDialogService: EurotaxListDialogService, private messageService: MessageService, private eurotaxService: EurotaxService,
    public dataService: DataService) {
    this.expertMode = getExpertMode(this.route);
    this.motorrad = getMotorrad(this.route);
    this.offertBase = getOffertBase(this.route);
    this.verifyOffert = getVerifyOffert(this.route);

    this.marken.subscribe((res) => {
      let markeGroupOptions = valueGroupOptions(of(res).pipe(
        flatMap(res => res), map(res => ({ category: res.Value.substring(0, 1), value: res })),
        catchError((err: HttpErrorResponse) => {
          this.popupService.openErrorPopup(this.messageService.generateErrorMessage(ErrorMessageType.General));
          console.log(err.message); return throwError(err);
        })), this.marke);
        markeGroupOptions = combineLatest([markeGroupOptions,
          of([
            {
              letter: 'Sonstiges',
              values: [
                { Key: '-1', Id: '-1', Value: 'Sonstige Marke (bitte eingeben)' },
              ],
            },
          ])
        ]).pipe(
          map(([markenGroupOptions, sonstigesGroupOptions]) => [...markenGroupOptions, ...sonstigesGroupOptions])
        );
      markeGroupOptions.pipe(first()).toPromise().then(() => {
        if (this.marke.value) {
          this.marke.setValue(null);
        }
      });
      this.markeGroupOptions = markeGroupOptions;
    });
  }

  static createFormGroup(fb: UntypedFormBuilder, motorrad: boolean) {
    return fb.group({
      fahrzeugart: new UntypedFormControl('', Validators.required),
      marke: new UntypedFormControl('', CustomValidator.requiredLookupValue),
      sonstigeMarke: new UntypedFormControl('', Validators.required),
      modell: new UntypedFormControl('', Validators.required),
      erstzulassungsdatumGroup: fb.group({
        erstzulassungsdatum: new UntypedFormControl('', Validators.required),
        baujahrBeginn: new UntypedFormControl(''),
        baujahrEnde: new UntypedFormControl('')
      }, { validators: [CustomValidator.eurotaxBaujahr] }),
      emissionCo2: new UntypedFormControl('', [Validators.required, Validators.pattern('^[0-9]*$'), Validators.min(1), Validators.max(9999)]),
      leistungPs: new UntypedFormControl('', [Validators.pattern('^[0-9]*$'), Validators.min(1), Validators.max(9999)]),
      leistungKw: new UntypedFormControl('', [motorrad ? Validators.nullValidator : Validators.required,
        Validators.pattern('^[0-9]*$'), Validators.min(1), Validators.max(9999)]),
      verwendungsbestimmung: new UntypedFormControl('', Validators.required),
      nutzlastKg: new UntypedFormControl('', [Validators.pattern('^[0-9]*$'), Validators.min(1),
        CustomValidator.maxNutzlastLKW, Validators.max(10000000)]),
      gesamtgewichtKg: new UntypedFormControl('', [Validators.required, Validators.pattern('^[0-9]*$'), Validators.min(1),
        CustomValidator.maxGesamtgewicht, Validators.max(10000000)]),
      hubraumCcm: new UntypedFormControl('', [Validators.required, Validators.pattern('^[0-9]*$'), Validators.min(1), CustomValidator.maxCcmMotorfahrrad, Validators.max(9999) ]),
      hoechstGeschwindigkeitKmh: new UntypedFormControl('', [Validators.required, Validators.pattern('^[0-9]*$'), Validators.min(1), Validators.max(9999)]),
      anzahlPlaetze: new UntypedFormControl('', Validators.required),
      antriebsart: new UntypedFormControl('', Validators.required),
      andereAntriebsart: new UntypedFormControl('', Validators.required),
      fahrgestellnummer: new UntypedFormControl('', Validators.pattern('[a-zA-Z0-9]*$')),
      verwendungsbestimmungJaNein: new UntypedFormControl('', Validators.required),
      eigengewichtKg: new UntypedFormControl('', [Validators.required, Validators.pattern('^[0-9]*$'), Validators.min(1), Validators.max(9999)]),
      hybridart: new UntypedFormControl('', Validators.required)
    });
  }

  ngOnInit() {
      if(!this.complete) {
        this.onValueChanged();
      }
      this.toggleEditMode();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.privatperson && changes.privatperson.currentValue !== changes.privatperson.previousValue && changes.privatperson.currentValue !== null && !this.complete) {
      this.getFahrzeugarten().pipe(first()).toPromise().then(res => {
        if (this.fahrzeugart.value && !res.find(x => x.Id === this.fahrzeugart.value.Id)) {
          this.fahrzeugart.setValue(null);
        }
      });
    }
    if (changes.selected && changes.selected.previousValue === false && changes.selected.currentValue === true) {
      Promise.resolve().then(() => this.toggleEditMode());
    }
  }

  get fahrzeugart() { return this.formGroup.get('fahrzeugart'); }
  get marke() { return this.formGroup.get('marke'); }
  get sonstigeMarke() { return this.formGroup.get('sonstigeMarke'); }
  get modell() { return this.formGroup.get('modell'); }
  get erstzulassungsdatumGroup() { return this.formGroup.get('erstzulassungsdatumGroup'); }
  get erstzulassungsdatum() { return this.erstzulassungsdatumGroup.get('erstzulassungsdatum'); }
  get baujahrBeginn() { return this.erstzulassungsdatumGroup.get('baujahrBeginn'); }
  get baujahrEnde() { return this.erstzulassungsdatumGroup.get('baujahrEnde'); }
  get emissionCo2() { return this.formGroup.get('emissionCo2'); }
  get leistungPs() { return this.formGroup.get('leistungPs'); }
  get leistungKw() { return this.formGroup.get('leistungKw'); }
  get verwendungsbestimmung() { return this.formGroup.get('verwendungsbestimmung'); }
  get nutzlastKg() { return this.formGroup.get('nutzlastKg'); }
  get gesamtgewichtKg() { return this.formGroup.get('gesamtgewichtKg'); }
  get hubraumCcm() { return this.formGroup.get('hubraumCcm'); }
  get hoechstGeschwindigkeitKmh() { return this.formGroup.get('hoechstGeschwindigkeitKmh'); }
  get anzahlPlaetze() { return this.formGroup.get('anzahlPlaetze'); }
  get antriebsart() { return this.formGroup.get('antriebsart'); }
  get andereAntriebsart() { return this.formGroup.get('andereAntriebsart'); }
  get fahrgestellnummer() { return this.formGroup.get('fahrgestellnummer'); }
  get verwendungsbestimmungJaNein() { return this.formGroup.get('verwendungsbestimmungJaNein'); }
  get eigengewichtKg() { return this.formGroup.get('eigengewichtKg'); }
  get hybridart() { return this.formGroup.get('hybridart'); }

  get showSonstigeMarke() {
    return this.marke.value && this.marke.value.Key === '-1' ? true : false;
  }
  get showEmissionCo2() {
    return this.erstzulassungsdatum.value && this.erstzulassungsdatum.value.toDate() >= this.erstzulassungsdatumCo2 &&
      (!this.motorrad && this.fahrzeugart.value && (this.fahrzeugart.value.Key === '15' || this.fahrzeugart.value.Key === '22') ||
        this.motorrad && this.fahrzeugart.value && (this.fahrzeugart.value.Key === '12' || this.fahrzeugart.value.Key === '9')) &&
      this.antriebsart.value && this.antriebsart.value.Key !== '6' ? true : false;
  }
  get showLeistungPs() {
    return this.fahrzeugart.value && this.fahrzeugart.value.Key !== '1' && this.fahrzeugart.value.Key !== '23' ? true : false;
  }
  get showLeistungKw() {
    return this.showLeistungPs;
  }
  get showVerwendungsbestimmung() {
    return false;
  }
  get showVerwendungsbestimmungJaNein() {
    return !this.complete && this.fahrzeugart.value && (this.fahrzeugart.value.Key === '6' || this.fahrzeugart.value.Key === '15' ||
      this.fahrzeugart.value.Key === '22' || this.fahrzeugart.value.Key === '23') ? true : false;
  }
  get isZugMaschineSelected(){
    return this.fahrzeugart.value && this.fahrzeugart.value.Key === '23' ? true : false;
  }
  get showNutzlastKg() {
    return this.fahrzeugart.value && (this.fahrzeugart.value.Key === '6' || this.fahrzeugart.value.Key === '7' || this.fahrzeugart.value.Key === '8' ||
      this.fahrzeugart.value.Key === '1' || this.fahrzeugart.value.Key === '22') ? true : false;
  }
  get showGesamtgewichtKg() {
    return this.showNutzlastKg;
  }
  get showHubraumCcm() {
    return this.motorrad && this.antriebsart.value && this.antriebsart.value.Key !== '6' ? true : false;
  }
  get showHoechstGeschwindigkeitKmh() {
    return this.motorrad && this.antriebsart.value && this.antriebsart.value.Key === '6' ? true : false;
  }
  get showAnzahlPlaetze() {
    return this.fahrzeugart.value && this.fahrzeugart.value.Key === '10' ? true : false;
  }
  get showAntriebsart() {
    return this.motorrad || (this.fahrzeugart.value && (this.fahrzeugart.value.Key === '15' ||
      this.fahrzeugart.value.Key === '6' || this.fahrzeugart.value.Key === '22' ||
      this.fahrzeugart.value.Key === '7' || this.fahrzeugart.value.Key === '8')) ? true : false;
  }
  get showAndereAntriebsart() {
    return this.showAntriebsart && this.antriebsart.value && this.antriebsart.value.Key === '8' ? true : false;
  }
  get showFahrgestellnummer() {
    return this.complete;
  }
  get showEurotaxAbfrage() {
    return this.fahrzeugart.value &&
      (this.fahrzeugart.value.Key === '15' || this.fahrzeugart.value.Key === '12') && !this.complete;
  }

  get showMbvInfoWohnMobile() {
    return this.erstzulassungsdatum.value > moment(new Date(2020, 9, 1)) && this.fahrzeugart.value && this.fahrzeugart.value.Key === '22' ? true : false;
  }
  get showEigengewichtKg() {
    return this.fahrzeugart.value && this.fahrzeugart.value.Key === '15' &&
      this.antriebsart.value && this.antriebsart.value.Key === '6' ? true : false;
  }
  get showHybridart() {
    return this.fahrzeugart.value && this.fahrzeugart.value.Key === '15' &&
      this.antriebsart.value && (this.antriebsart.value.Key === '13' || this.antriebsart.value.Key === '15') ? true : false;
  }
  get zugmaschineVerwendungsbestimmungInfoKey(){
    return this.isZugMaschineSelected ? 'VERWENDUNGSBESTIMMUNGJANEINHINTZUGMASCHINE' : 'VERWENDUNGSBESTIMMUNGJANEINHINT';
  }

  getFahrzeugarten() {
    let fahrzeugarten: Observable<ILookupValue[]>;
    if (this.firma || this.einpersonenUnternehmen) {
      if (this.privatperson) {
        fahrzeugarten = this.fahrzeugarten.pipe(map(fahrzeugarten => fahrzeugarten.filter(x => x.Key !== '7' && x.Key !== '8')));
      } else {
        if(this.einpersonenUnternehmen){ // EP Unternehmen sollen alle 3 LKW Arten auswählen können
          fahrzeugarten = this.fahrzeugarten.asObservable();
        }else{
          fahrzeugarten = this.fahrzeugarten.pipe(map(fahrzeugarten => fahrzeugarten.filter(x => x.Key !== '6')));
        }
      }
    } else {
      if (this.privatperson) {
        fahrzeugarten = this.fahrzeugarten.pipe(map(fahrzeugarten => fahrzeugarten.filter(x => x.Key !== '7' && x.Key !== '8')));
      } else {
        fahrzeugarten = this.fahrzeugarten.asObservable();
      }
    }
    const fahrzeugart = this.risikoKfzComponents?.first.fahrzeugart.value;
    if (this.motorrad && this.risikoIndex > 0) {
      if (fahrzeugart?.Key === '12') {
        return this.fahrzeugarten.pipe(
          map(fahrzeugarten => fahrzeugarten.filter(x => x.Key === '12'))
        );
      } else {
        return of([]);
      }
    }
    if (!this.motorrad && this.risikoIndex > 0) {
      if (fahrzeugart?.Key === '15') {
        return fahrzeugarten.pipe(
          map(fahrzeugarten => fahrzeugarten.filter(x => x.Key === '15'))
        );
      } else {
        return of([]);
      }
    }
    return fahrzeugarten;
  }

  get risikoIndex() {
    return this.risikoKfzComponents?.toArray().indexOf(this);
  }

  get showHeader() {
    return this.complete && this.risikoKfzComponents?.length > 1;
  }

  preselectFahrzeugart(): void {
    this.fahrzeugarten.pipe(first()).toPromise().then((fahrzeugarten) => {
      if (this.motorrad) {
        if (this.risikoKfzComponents.first.fahrzeugart.value.Key === '12') {
          this.fahrzeugart.setValue(fahrzeugarten.find(x => x.Key === '12'));
        }
      } else {
        if (this.risikoKfzComponents.first.fahrzeugart.value.Key === '15') {
          this.fahrzeugart.setValue(fahrzeugarten.find(x => x.Key === '15'));
        }
      }
    });
  }

  toggleEditMode(): void {
    toggleEditMode(this.sonstigeMarke, this.showSonstigeMarke, this.complete);
    toggleEditMode(this.leistungPs, this.showLeistungPs, this.eurotax || this.complete);
    toggleEditMode(this.leistungKw, this.showLeistungKw, this.eurotax || this.complete);
    toggleEditMode(this.verwendungsbestimmung, this.showVerwendungsbestimmung);
    toggleEditMode(this.verwendungsbestimmungJaNein, this.showVerwendungsbestimmungJaNein, false, !this.complete);
    toggleEditMode(this.nutzlastKg, this.showNutzlastKg);
    toggleEditMode(this.gesamtgewichtKg, this.showGesamtgewichtKg);
    toggleEditMode(this.hubraumCcm, this.showHubraumCcm, this.eurotax || this.complete);
    toggleEditMode(this.hoechstGeschwindigkeitKmh, this.showHoechstGeschwindigkeitKmh);
    toggleEditMode(this.anzahlPlaetze, this.showAnzahlPlaetze);
    toggleEditMode(this.antriebsart, this.showAntriebsart, this.eurotax || this.complete);
    toggleEditMode(this.andereAntriebsart, this.showAndereAntriebsart);
    toggleEditMode(this.emissionCo2, this.showEmissionCo2);
    toggleEditMode(this.fahrgestellnummer, this.showFahrgestellnummer, false);
    toggleEditMode(this.eigengewichtKg, this.showEigengewichtKg);
    toggleEditMode(this.hybridart, this.showHybridart);
  }

  loadMarken(): void {
    if (this.fahrzeugart.valid) {
      this.calculationService.getKfzMarken({
        Motorrad: this.motorrad,
        FahrzeugArtId: Number(this.fahrzeugart.value.Id)
      }).subscribe(
        data => {
          this.marken.next(data.KfzMarken);
        },
        (err: HttpErrorResponse) => {
          this.popupService.openErrorPopup(this.messageService.generateErrorMessage(ErrorMessageType.General));
          console.log(err.message);
        }
      );
    }
  }

  onValueChanged(): void {
    this.fahrzeugart.valueChanges.subscribe(() => {
      if (this.risikoIndex === 0) {
        this.risikoKfzComponents.forEach((risikoKfzComponent, index) => {
          if (index > 0) {
            risikoKfzComponent.getFahrzeugarten().pipe(first()).toPromise().then(res => {
              if (risikoKfzComponent.fahrzeugart.value && !res.find(x => x.Id === risikoKfzComponent.fahrzeugart.value.Id)) {
                risikoKfzComponent.fahrzeugart.setValue(null);
              }
              if (this.fahrzeugart.value.Key === '1') {
                risikoKfzComponent.fahrzeugart.setValue(res.find(x => x.Key === '1'));
              } else if (this.fahrzeugart.value.Key === '10') {
                risikoKfzComponent.fahrzeugart.setValue(res.find(x => x.Key === '10'));
              }
            });
          }
        });
      }
      const key = this.fahrzeugart.value?.Key;
      if (['1', '6', '7', '8'].includes(key)) {
        this.nutzlastKg.addValidators(Validators.required);
        } else {
          this.nutzlastKg.removeValidators(Validators.required);
        }
      this.nutzlastKg.updateValueAndValidity();

      if (this.eurotax) {
        this.resetEurotax();
      }

      this.loadMarken();
      this.fahrzeugartChangedEvent.emit();
      if (this.fahrzeugart.value?.Key === '23') {
        this.verwendungsbestimmungJaNein.removeValidators(CustomValidator.requiredNo);
        this.verwendungsbestimmungJaNein.addValidators(CustomValidator.requiredYes);
      } else {
        this.verwendungsbestimmungJaNein.removeValidators(CustomValidator.requiredYes);
        this.verwendungsbestimmungJaNein.addValidators(CustomValidator.requiredNo);
      }
      this.verwendungsbestimmungJaNein.updateValueAndValidity();
    });

    merge(
      this.fahrzeugart.valueChanges,
      this.marke.valueChanges,
      this.antriebsart.valueChanges,
      this.erstzulassungsdatum.valueChanges,
    ).subscribe(() => {
      this.toggleEditMode();
    });

    merge(
      this.fahrzeugart.valueChanges,
      this.marke.valueChanges,
      this.modell.valueChanges,
      this.erstzulassungsdatum.valueChanges,
      this.emissionCo2.valueChanges,
      this.leistungPs.valueChanges,
      this.leistungKw.valueChanges,
      this.eigengewichtKg.valueChanges,
      this.verwendungsbestimmung.valueChanges,
      this.verwendungsbestimmungJaNein.valueChanges,
      this.nutzlastKg.valueChanges,
      this.gesamtgewichtKg.valueChanges,
      this.hubraumCcm.valueChanges,
      this.hoechstGeschwindigkeitKmh.valueChanges,
      this.anzahlPlaetze.valueChanges,
      this.antriebsart.valueChanges,
      this.andereAntriebsart.valueChanges,
      this.hybridart.valueChanges
    ).subscribe(() => {
      this.parameterChangedEvent.emit();
    });

    this.leistungPs.valueChanges.subscribe(() => {
      const options = { emitEvent: false };
      if (this.leistungPs.value && this.leistungPs.valid) {
        this.leistungKw.setValue(Math.round(this.leistungPs.value / 1.36), options);
      } else {
        this.leistungKw.setValue('', options);
      }
    });

    this.leistungKw.valueChanges.subscribe(() => {
      const options = { emitEvent: false };
      if (this.leistungKw.valid) {
        this.leistungPs.setValue(Math.round(this.leistungKw.value * 1.36), options);
      } else {
        this.leistungPs.setValue('', options);
      }
    });

    this.verwendungsbestimmungJaNein.valueChanges.subscribe(() => {
      if(this.verwendungsbestimmungJaNein.valid){
        if(this.verwendungsbestimmungJaNein.value === 'nein'){
          this.verwendungsbestimmung.setValue({ Id: 1});
        }else{
          this.verwendungsbestimmung.setValue(null);
        }
      }
    });

    merge(
      this.fahrzeugart.valueChanges,
      this.erstzulassungsdatum.valueChanges).subscribe(() => {
    });

    this.antriebsart.valueChanges.subscribe(() => {
      if (!this.offertBase && !this.verifyOffert && this.antriebsart.value?.Key === '6') {
        this.translateService.get(this.expertMode ? 'LEISTUNGELEKTROPRAEMIEEXPERTINFO' : 'LEISTUNGELEKTROPRAEMIEINFO')
          .subscribe((text) => {
          this.popupService.openInfoPopup(text);
        });
      }
      if (this.motorrad) {
        if (this.antriebsart.value?.Key === '6') {
        this.leistungKw.addValidators(Validators.required);
        } else {
          this.leistungKw.removeValidators(Validators.required);
        }
        this.leistungKw.updateValueAndValidity();
      }
    });
  }

  setMarke(risiko: IKfzRisiko) {
    this.markeGroupOptions.pipe(first()).toPromise().then((markenGroupOptions) => {
      this.marke.setValue(markenGroupOptions.map(x => x.values).reduce((acc, val) => acc.concat(val), [])
        .find(x => x.Id === risiko.MarkeId.toString()));
      this.sonstigeMarke.setValue(risiko.SonstigeMarke);
    });
  }

  setEurotaxEditMode() {
    const kaskoKfzComponent = this.kaskoKfzComponents.toArray()[this.risikoIndex];
    const options = { emitEvent: false };

    this.marke.disable(options);
    this.modell.disable(options);

    if (this.showHubraumCcm) {
      this.hubraumCcm.disable(options);
    }
    if (this.leistungKw.value &&
      this.leistungPs.value &&
      this.antriebsart.value?.Key !== '6') {
      if (this.showLeistungKw) {
        this.leistungKw.disable(options);
      }
      if (this.showLeistungPs) {
        this.leistungPs.disable(options);
      }
    } else {
      if (this.showLeistungKw) {
        this.leistungKw.enable(options);
      }
      if (this.showLeistungPs) {
        this.leistungPs.enable(options);
      }
    }
    kaskoKfzComponent.setEurotaxEditMode();
  }

  setEurotax(result: IEuroTaxResult, mapping: IEuroTaxMappingResult) {
    const options = { emitEvent: false };

    this.eurotax = true;

    if (this.eurotaxMarke) {
      this.eurotaxMarke.unsubscribe();
    }
    if (this.eurotaxAntriebsart) {
      this.eurotaxAntriebsart.unsubscribe();
    }

    this.eurotaxMarke = this.marken.pipe(shareReplay(1)).subscribe((res) => {
      this.marke.setValue(res.find(x => x.Id === mapping.MarkeId?.toString()));
    });
    this.eurotaxAntriebsart = this.antriebsart.valueChanges.subscribe(() => {
      if (this.showLeistungKw) {
        this.leistungKw.setValue(Math.ceil(result.nennleistungKw), options);
      }
      if (this.showLeistungPs) {
        this.leistungPs.setValue(Math.ceil(result.nennleistungPs), options);
      }
      if (this.showHubraumCcm) {
        this.hubraumCcm.setValue(result.typCapTech, options);
      }
      this.setEurotaxEditMode();
    });

    if (this.showLeistungKw) {
      this.leistungKw.setValue(Math.ceil(result.nennleistungKw), options);
    }
    if (this.showLeistungPs) {
      this.leistungPs.setValue(Math.ceil(result.nennleistungPs), options);
    }
    if (this.showHubraumCcm) {
      this.hubraumCcm.setValue(result.typCapTech, options);
    }

    this.modell.setValue(result.typBezeichnung);

    const kaskoKfzComponent = this.kaskoKfzComponents.toArray()[this.risikoIndex];
    kaskoKfzComponent.setEurotax(result);

    this.baujahrBeginn.setValue(Number(result.modellImportBeginn.substring(0, 4)));
    this.baujahrEnde.setValue(result.modellImportEnde ? Number(result.modellImportEnde.substring(0, 4)) : null);

    this.setEurotaxEditMode();
  }

  resetEurotax() {
    if (this.risikoIndex !== -1) {

      const options = { emitEvent: false };

      this.eurotax = false;

      if (this.eurotaxMarke) {
        this.eurotaxMarke.unsubscribe();
      }
      if (this.eurotaxAntriebsart) {
        this.eurotaxAntriebsart.unsubscribe();
      }

      this.marke.enable(options);
      this.modell.setValue(null);
      this.modell.enable(options);

      if (this.showLeistungKw) {
        this.leistungKw.setValue(null);
        this.leistungKw.enable();
      }
      if (this.showLeistungPs) {
        this.leistungPs.setValue(null);
        this.leistungPs.enable();
      }
      if (this.showHubraumCcm) {
        this.hubraumCcm.setValue(null);
        this.hubraumCcm.enable();
      }

      const kaskoKfzComponent = this.kaskoKfzComponents.toArray()[this.risikoIndex];
      kaskoKfzComponent.resetEurotax();

      this.baujahrBeginn.setValue(null);
      this.baujahrEnde.setValue(null);
    }
  }

   pingEurotax(){
    this.eurotaxService.pingService().subscribe(() => {}, () => {
        this.popupService.openErrorPopup(this.messageService.generateErrorMessage(ErrorMessageType.EurotaxService));
      });
  }

   loadEurotax(result: IEuroTaxResult) {
     this.calculationService.getEurotaxMapping(this.motorrad ? 40 : 10, result.markenName, result.typtxtFuelTYPeCd2).subscribe(
      data => {
        this.setEurotax(result, data);
      },
      (err: HttpErrorResponse) => {
        this.popupService.openErrorPopup(this.messageService.generateErrorMessage(ErrorMessageType.General));
        console.log(err.message);
      }
    );
  }

  setRisiko(risiko: IKfzRisiko) {
    const options = { emitEvent: false };
    return new Promise<void>(resolve => {
      this.fahrzeugarten.pipe(first()).toPromise().then((fahrzeugarten) => {
        this.fahrzeugart.setValue(fahrzeugarten.find(x => x.Id === risiko.FahrzeugArtId?.toString()));
        // Wegen der Vorbelegung der Fahrzeugart werden die Marken zweimal bezogen
        if (this.risikoIndex > 0) {
          this.marken.pipe(take(2)).toPromise().then(() => {
            this.setMarke(risiko);
          });
        } else {
          this.marken.pipe(first()).toPromise().then(() => {
            this.setMarke(risiko);
          });
        }
        this.modell.setValue(risiko.FahrzeugType);
        if (risiko.VerwendungsbestimmungId) {
          this.verwendungsbestimmungen.pipe(first()).toPromise().then((verwendungsbestimmungen) => {
            this.verwendungsbestimmung.setValue(verwendungsbestimmungen.find(x => x.Id === risiko.VerwendungsbestimmungId.toString()));
          });
        }
        if (this.isZugMaschineSelected) {
          this.verwendungsbestimmungJaNein.setValue('ja');
        } else {
          this.verwendungsbestimmungJaNein.setValue('nein');
        }
        this.antriebsarten.pipe(first()).toPromise().then((antriebsarten) => {
          this.antriebsart.setValue(antriebsarten.find(x => x.Id === risiko.AntriebsArtId?.toString()));
          this.erstzulassungsdatum.setValue(moment(risiko.Erstzulassungsdatum));
          this.emissionCo2.setValue(risiko.EmissionCo2);
          this.leistungPs.setValue(risiko.LeistungPs, options);
          this.leistungKw.setValue(risiko.LeistungKw, options);
          this.eigengewichtKg.setValue(risiko.EigengewichtKg);
          this.nutzlastKg.setValue(risiko.NutzlastKg);
          this.gesamtgewichtKg.setValue(risiko.GesamtgewichtKg);
          this.hubraumCcm.setValue(risiko.HubraumCcm);
          this.hoechstGeschwindigkeitKmh.setValue(risiko.HoechstGeschwindigkeitKmh);
          this.anzahlPlaetze.setValue(risiko.AnzahlPlaetze ? risiko.AnzahlPlaetze.toString() : null);
          this.andereAntriebsart.setValue(risiko.AndereAntriebsart);
          this.hybridart.setValue(risiko.Hybridart ? risiko.Hybridart.toString() : null);
          this.fahrgestellnummer.setValue(risiko.Fahrgestellnummer);
          this.eurotax = risiko.Eurotax;
          if (risiko.Eurotax) {
            this.setEurotaxEditMode();
          }
          resolve();
        });
      });
    });
  }

  getRisiko(): IKfzRisiko {
    return {
      FahrzeugArtId: Number(this.fahrzeugart.value.Id),
      MarkeId: Number(this.marke.value.Id),
      SonstigeMarke: this.sonstigeMarke.value,
      FahrzeugType: this.modell.value,
      Erstzulassungsdatum: this.erstzulassungsdatum.value,
      EmissionCo2: this.emissionCo2.value,
      LeistungKw: this.leistungKw.value,
      LeistungPs: this.leistungPs.value,
      EigengewichtKg: this.eigengewichtKg.value,
      VerwendungsbestimmungId: this.fahrzeugart.value.Key === '23' ? 2 : this.verwendungsbestimmungJaNein.value === 'nein' ? 1 : null,
      NutzlastKg: this.nutzlastKg.value,
      GesamtgewichtKg: this.gesamtgewichtKg.value,
      HubraumCcm: this.hubraumCcm.value,
      HoechstGeschwindigkeitKmh: this.hoechstGeschwindigkeitKmh.value,
      AnzahlPlaetze: this.anzahlPlaetze.value ? this.anzahlPlaetze.value : null,
      AntriebsArtId: this.antriebsart.value ? Number(this.antriebsart.value.Id) : null,
      AndereAntriebsart: this.andereAntriebsart.value,
      Hybridart: this.hybridart.value ? Number(this.hybridart.value) : null,
      Fahrgestellnummer: this.fahrgestellnummer.value,
      Eurotax: this.eurotax
    } as IKfzRisiko;
  }
}
